import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ModulePermissionDto } from "../utils/rolePermissions";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  companyId?: string | null;
  branchId?: string;
  salesPointId: string;
  isActive: boolean;
  /** Raw grants stored on user, e.g. { balance_transfer: true } */
  featurePermissions?: Record<string, boolean>;
  /** Resolved feature keys from API (login/`me` top-level) */
  effectiveFeaturePermissions?: string[];
  /**
   * Backend RBAC matrix — source of truth for UI gates.
   * Always loaded from login /auth/me — never trusted from localStorage.
   */
  permissions?: ModulePermissionDto[];
  company?: {
    id: string;
    name: string;
    code?: string;
    gstin?: string | null;
    pan?: string | null;
    stateCode?: string | null;
    businessType?: string | null;
    dlNumber?: string | null;
    fssaiNumber?: string | null;
    country?: string | null;
    currencyCode?: string | null;
    currencySymbol?: string | null;
    taxRegime?: string | null;
    taxLabel?: string | null;
    taxNumberLabel?: string | null;
  } | null;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  _hasHydrated: boolean;
  setHasHydrated: (v: boolean) => void;
  login: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  updateUser: (user: Partial<AuthUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      _hasHydrated: false,
      setHasHydrated: (v) => set({ _hasHydrated: v }),
      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),
      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),
      updateUser: (userData) =>
        set((state) => {
          if (!state.user) {
            if (
              userData &&
              typeof userData === "object" &&
              "id" in userData &&
              userData.id
            ) {
              return {
                user: userData as AuthUser,
                isAuthenticated: true,
              };
            }
            return {};
          }
          return {
            user: { ...state.user, ...userData },
          };
        }),
    }),
    {
      // New key drops stale auth-storage that wiped permissions on rehydrate.
      name: "paytool-auth-v2",
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
